import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, UserPlus, Users, ShieldCheck, Search,
  FileText, Download, Trash2, MoreVertical, FolderPlus, ChevronRight, Edit2,
  Home, Eye, Activity, Flag, MessageSquare, History,
} from 'lucide-react';
import { dataRoomAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FolderTree from './components/FolderTree';
import InviteUserModal from './components/InviteUserModal';
import RolesModal from './components/RolesModal';
import PermissionsMatrixModal from './components/PermissionsMatrixModal';
import MembersPanel from './components/MembersPanel';
import FilePreviewModal from './components/FilePreviewModal';
import ActivityDashboardModal from './components/ActivityDashboardModal';
import QAModal from './components/QAModal';
import GlobalSearchModal from './components/GlobalSearchModal';
import VersionHistoryModal from './components/VersionHistoryModal';
import { multipartUpload, MULTIPART_THRESHOLD } from './components/multipartUpload';

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleDateString(); } catch { return '--'; }
}

export default function DataRoomWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('root');
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [showInvite, setShowInvite] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [showQA, setShowQA] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [versionsFile, setVersionsFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  const loadAll = useCallback(async () => {
    try {
      const [roomRes, folderRes] = await Promise.all([
        dataRoomAPI.get(id),
        dataRoomAPI.listFolders(id),
      ]);
      setRoom(roomRes.data);
      setFolders(folderRes.data.data || []);
    } catch (err) {
      console.error(err);
      alert('Failed to load data room');
    }
  }, [id]);

  const loadFiles = useCallback(async () => {
    try {
      const res = await dataRoomAPI.listFiles(id, selectedFolderId);
      setFiles(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [id, selectedFolderId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  // Keyboard shortcut: Ctrl/Cmd+K opens global search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleCreateRootFolder = async () => {
    const name = window.prompt('Folder name:');
    if (!name) return;
    try {
      await dataRoomAPI.createFolder(id, { name, parent_id: null });
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleCreateSubfolder = async (parentId) => {
    const name = window.prompt('Subfolder name:');
    if (!name) return;
    try {
      await dataRoomAPI.createFolder(id, { name, parent_id: parentId });
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleRenameFolder = async (folder) => {
    const name = window.prompt('Rename folder:', folder.name);
    if (!name || name === folder.name) return;
    try {
      await dataRoomAPI.updateFolder(id, folder.id, { name });
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete "${folder.name}" and all its contents?`)) return;
    try {
      await dataRoomAPI.deleteFolder(id, folder.id);
      if (selectedFolderId === folder.id) setSelectedFolderId('root');
      loadAll();
      loadFiles();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleUpload = async (fileList) => {
    const filesArr = Array.from(fileList || []);
    if (!filesArr.length) return;
    setUploading(true);
    setUploadProgress(0);
    const folderIdArg = selectedFolderId === 'root' ? null : selectedFolderId;
    let done = 0;
    for (const f of filesArr) {
      try {
        if (f.size >= MULTIPART_THRESHOLD) {
          // Resumable multipart upload for large files
          await multipartUpload({
            dataRoomId: id,
            file: f,
            folderId: folderIdArg,
            onProgress: (pct) => {
              const overall = Math.round(((done + pct / 100) / filesArr.length) * 100);
              setUploadProgress(overall);
            },
          });
        } else {
          await dataRoomAPI.uploadFile(id, f, folderIdArg, (evt) => {
            const pct = Math.round((evt.loaded * 100) / (evt.total || evt.loaded));
            const overall = Math.round(((done + pct / 100) / filesArr.length) * 100);
            setUploadProgress(overall);
          });
        }
        done++;
      } catch (err) {
        console.error('Upload failed', err);
        alert(`Failed to upload ${f.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    loadFiles();
  };

  const handleDownload = async (file) => {
    try {
      const res = await dataRoomAPI.getDownloadUrl(id, file.id);
      const link = document.createElement('a');
      link.href = res.data.url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to get download URL');
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.name}"?`)) return;
    try {
      await dataRoomAPI.deleteFile(id, file.id);
      loadFiles();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleRenameFile = async (file) => {
    const name = window.prompt('New file name:', file.name);
    if (!name || name === file.name) return;
    try {
      await dataRoomAPI.renameFile(id, file.id, { name });
      loadFiles();
    } catch (err) {
      alert('Failed');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!canEdit) return;
    handleUpload(e.dataTransfer.files);
  };

  // Breadcrumb path
  const folderMap = {};
  folders.forEach(f => { folderMap[f.id] = f; });
  const buildBreadcrumb = (folderId) => {
    if (!folderId || folderId === 'root') return [];
    const path = [];
    let cur = folderMap[folderId];
    while (cur) {
      path.unshift(cur);
      cur = cur.parent_id ? folderMap[cur.parent_id] : null;
    }
    return path;
  };
  const breadcrumb = buildBreadcrumb(selectedFolderId);

  const filteredFiles = files;

  const selectedFile = files.find(f => f.id === selectedFileId);

  if (!room) return <div className="p-8 text-center text-gray-500">Loading data room...</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="data-room-workspace">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={() => navigate('/data-rooms')}
            className="p-1.5 text-gray-400 hover:text-gray-700"
            data-testid="back-to-list"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate" data-testid="room-name">{room.name}</h1>
            <p className="text-xs text-gray-500 truncate" data-testid="room-subtitle">
              {room.link_type === 'fund'
                ? (room.fund?.name ? `Fund: ${room.fund.name}` : 'Fund')
                : room.link_type === 'other'
                ? (room.custom_reason || 'Other')
                : (room.investment?.company_name || '')}
              {room.description ? ` • ${room.description}` : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSearch(true)}
            className="inline-flex items-center px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50 text-gray-600"
            data-testid="open-search-btn"
            title="Search everything (Ctrl+K)"
          >
            <Search size={13} className="mr-1" /> Search
          </button>

          {canEdit && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 disabled:opacity-50"
                data-testid="upload-btn"
              >
                <Upload size={13} className="mr-1" />
                {uploading ? `Uploading ${uploadProgress}%` : 'Upload'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={e => handleUpload(e.target.files)}
              />
              <button
                onClick={() => setShowMembers(true)}
                className="inline-flex items-center px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50"
                data-testid="members-btn"
              >
                <Users size={13} className="mr-1" /> Members
              </button>
              <button
                onClick={() => setShowActivity(true)}
                className="inline-flex items-center px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50"
                data-testid="activity-btn"
              >
                <Activity size={13} className="mr-1" /> Activity
              </button>
              <button
                onClick={() => setShowQA(true)}
                className="inline-flex items-center px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50"
                data-testid="qa-btn"
              >
                <MessageSquare size={13} className="mr-1" /> Q&A
              </button>
              <button
                onClick={() => setShowRoles(true)}
                className="inline-flex items-center px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50"
                data-testid="roles-btn"
              >
                <ShieldCheck size={13} className="mr-1" /> Roles
              </button>
            </>
          )}
        </div>
      </div>

      {/* 3-pane layout */}
      <div className="flex flex-1 min-h-0">
        {/* Left: Folder tree */}
        <aside className="w-60 border-r bg-gray-50 flex flex-col" data-testid="left-pane">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Folders</span>
            {canEdit && (
              <button
                onClick={handleCreateRootFolder}
                className="p-1 text-gray-500 hover:text-blue-600"
                title="New folder"
                data-testid="new-root-folder-btn"
              >
                <FolderPlus size={14} />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-auto p-2">
            <FolderTree
              folders={folders}
              selectedId={selectedFolderId}
              onSelect={setSelectedFolderId}
              onCreateSubfolder={handleCreateSubfolder}
              onRename={handleRenameFolder}
              onDelete={handleDeleteFolder}
              canEdit={canEdit}
            />
          </div>
        </aside>

        {/* Center: Files */}
        <main
          className="flex-1 min-w-0 flex flex-col bg-white"
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          data-testid="center-pane"
        >
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 px-4 py-2 border-b text-xs text-gray-500 bg-gray-50">
            <button onClick={() => setSelectedFolderId('root')} className="hover:text-blue-600" data-testid="breadcrumb-root">
              <Home size={12} className="inline mr-1" /> Root
            </button>
            {breadcrumb.map(b => (
              <React.Fragment key={b.id}>
                <ChevronRight size={10} />
                <button
                  onClick={() => setSelectedFolderId(b.id)}
                  className={`hover:text-blue-600 ${b.id === selectedFolderId ? 'text-gray-900 font-medium' : ''}`}
                >
                  {b.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          {uploading && (
            <div className="px-4 py-1 bg-blue-50 border-b">
              <div className="w-full h-1 bg-blue-200 rounded overflow-hidden">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {/* File list */}
          <div className="flex-1 overflow-auto">
            {filteredFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <FileText size={40} className="mb-2" />
                <p className="text-sm">No files in this folder</p>
                {canEdit && (
                  <p className="text-xs mt-1">
                    Click <strong>Upload</strong> or drag & drop files here
                  </p>
                )}
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="files-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Uploader</th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Version</th>
                    <th className="px-3 py-2 font-medium">Size</th>
                    <th className="px-3 py-2 font-medium">Last Accessed</th>
                    <th className="px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.map(f => (
                    <tr
                      key={f.id}
                      onClick={() => { setSelectedFileId(f.id); setPreviewFile(f); }}
                      className={`border-b hover:bg-gray-50 cursor-pointer ${selectedFileId === f.id ? 'bg-blue-50' : ''}`}
                      data-testid={`file-row-${f.id}`}
                    >
                      <td className="px-4 py-2 font-medium text-gray-900 flex items-center gap-2">
                        <FileText size={14} className="text-blue-500 shrink-0" />
                        <span className="truncate max-w-[300px]">{f.name}</span>
                        {f.is_high_value && <Flag size={11} className="text-amber-500 shrink-0" title="High-value" />}
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{f.uploader_name || '--'}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{formatDate(f.uploaded_at)}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">v{f.version || 1}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{formatSize(f.size)}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{f.last_accessed ? formatDate(f.last_accessed) : '--'}</td>
                      <td className="px-3 py-2 text-right" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleDownload(f)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Download"
                          data-testid={`download-${f.id}`}
                        >
                          <Download size={13} />
                        </button>
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleRenameFile(f)}
                              className="p-1 text-gray-400 hover:text-gray-700"
                              title="Rename"
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              onClick={() => setVersionsFile(f)}
                              className="p-1 text-gray-400 hover:text-indigo-600"
                              title="Versions"
                              data-testid={`versions-${f.id}`}
                            >
                              <History size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteFile(f)}
                              className="p-1 text-gray-400 hover:text-red-500"
                              title="Delete"
                              data-testid={`delete-file-${f.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>

        {/* Right: Details pane */}
        <aside className="w-72 border-l bg-gray-50 overflow-auto" data-testid="right-pane">
          {selectedFile ? (
            <div className="p-4 space-y-3">
              <div>
                <FileText size={32} className="text-blue-500 mb-2" />
                <h3 className="font-semibold text-gray-900 break-words">{selectedFile.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">Version {selectedFile.version}</p>
              </div>
              <div className="space-y-2 text-xs">
                <Detail k="Size" v={formatSize(selectedFile.size)} />
                <Detail k="Type" v={selectedFile.content_type || '--'} />
                <Detail k="Uploader" v={selectedFile.uploader_name} />
                <Detail k="Uploaded" v={formatDate(selectedFile.uploaded_at)} />
                <Detail k="Last Accessed" v={selectedFile.last_accessed ? formatDate(selectedFile.last_accessed) : 'Never'} />
              </div>
              <button
                onClick={() => handleDownload(selectedFile)}
                className="w-full inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
                data-testid="side-download-btn"
              >
                <Download size={13} className="mr-1" /> Download
              </button>
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-gray-400">
              <Eye size={28} className="mx-auto mb-2" />
              Select a file to see details
            </div>
          )}
        </aside>
      </div>

      {/* Modals */}
      <InviteUserModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        dataRoomId={id}
        onInvited={() => setShowInvite(false)}
      />
      <RolesModal open={showRoles} onClose={() => setShowRoles(false)} dataRoomId={id} />
      <PermissionsMatrixModal open={showMatrix} onClose={() => setShowMatrix(false)} dataRoomId={id} />
      <MembersPanel
        open={showMembers}
        onClose={() => setShowMembers(false)}
        dataRoomId={id}
        onOpenInvite={() => { setShowMembers(false); setShowInvite(true); }}
        onOpenMatrix={() => { setShowMembers(false); setShowMatrix(true); }}
      />
      <ActivityDashboardModal
        open={showActivity}
        onClose={() => setShowActivity(false)}
        dataRoomId={id}
      />
      <FilePreviewModal
        open={!!previewFile}
        onClose={() => { setPreviewFile(null); loadFiles(); }}
        dataRoomId={id}
        file={previewFile}
        canEdit={canEdit}
      />
      <QAModal
        open={showQA}
        onClose={() => setShowQA(false)}
        dataRoomId={id}
      />
      <GlobalSearchModal
        open={showSearch}
        onClose={() => setShowSearch(false)}
        dataRoomId={id}
        onSelectFile={(f) => { setSelectedFileId(f.id); setPreviewFile(f); }}
        onSelectFolder={(fid) => setSelectedFolderId(fid)}
      />
      <VersionHistoryModal
        open={!!versionsFile}
        onClose={() => setVersionsFile(null)}
        dataRoomId={id}
        file={versionsFile}
      />
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-gray-500">{k}</span>
      <span className="text-gray-900 text-right break-words">{v || '--'}</span>
    </div>
  );
}
